import React from 'react';
import { Grid, TextField } from '@mui/material';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import ReadOnlyField from './readonly-field';

interface EmailFieldProps<TFieldValues extends FieldValues> {
  /** The field name for the email address */
  readonly emailName: Path<TFieldValues>;
  /** The field name for the confirmation email (optional for single field mode) */
  readonly confirmationName?: Path<TFieldValues>;
  /** React Hook Form control object */
  readonly control: Control<TFieldValues>;
  /** Handler for paste events (usually to prevent pasting in confirmation field) */
  readonly handlePaste?: (e: React.ClipboardEvent) => void;
  /** Custom label for email field (defaults to "Email Address") */
  readonly emailLabel?: string;
  /** Custom label for confirmation field (defaults to "Confirm Email Address") */
  readonly confirmLabel?: string;
  /** Whether the field is required (defaults to true) */
  readonly required?: boolean;
  /** Size for small screens (Grid sm property) */
  readonly sm?: number | boolean;
  /** Whether to allow pasting in the confirmation field */
  readonly allowPaste?: boolean;
  /** Whether to allow autofill for email fields */
  readonly allowAutofill?: boolean;
  /** Whether the fields are in read-only mode */
  readonly isReadOnly?: boolean;
}

/**
 * A reusable component that provides email field(s) with consistent validation and behavior.
 * Can be used in single field mode or with a confirmation field.
 * Supports both editable and read-only modes.
 */
const EmailField = <TFieldValues extends FieldValues>({
  emailName,
  confirmationName,
  control,
  handlePaste,
  emailLabel = 'Email Address',
  confirmLabel = 'Confirm Email Address',
  required = true,
  sm,
  allowPaste = true,
  allowAutofill = true,
  isReadOnly = false,
}: EmailFieldProps<TFieldValues>) => {
  const gridProps = {
    xs: 12,
    ...(sm !== undefined ? { sm } : {}),
  };

  return (
    <>
      <Grid item {...gridProps}>
        <Controller
          name={emailName}
          control={control}
          render={({ field, fieldState }) => {
            // Only show error if field is required OR has a value and is invalid
            const showError = required ? !!fieldState.error : !!field.value && !!fieldState.error;

            return isReadOnly ? (
              <ReadOnlyField label={emailLabel} value={field.value || ''} />
            ) : (
              <TextField
                {...field}
                fullWidth
                label={emailLabel}
                type="email"
                error={showError}
                helperText={showError ? fieldState.error?.message : ''}
                required={required}
                autoComplete={allowAutofill ? 'email' : 'off'}
                inputProps={{
                  autoComplete: allowAutofill ? 'email' : 'new-email',
                }}
                onChange={e => {
                  // Handle empty string as undefined for optional fields
                  if (!required && e.target.value === '') {
                    field.onChange('');
                  } else {
                    field.onChange(e.target.value);
                  }
                }}
              />
            );
          }}
        />
      </Grid>

      {confirmationName && !isReadOnly && (
        <Grid item {...gridProps}>
          <Controller
            name={confirmationName}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label={confirmLabel}
                type="email"
                onPaste={allowPaste ? undefined : handlePaste}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required={required}
                autoComplete={allowAutofill ? 'email' : 'off'}
              />
            )}
          />
        </Grid>
      )}
    </>
  );
};

export default EmailField;
